import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, MessageCircle, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const SupportPage = ({ user }) => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const res = await apiClient.get('/support/my-tickets');
      setTickets(res.data);
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await apiClient.post('/support/create', {
        message: newMessage.trim(),
        category
      });
      toast.success('Solicitud enviada correctamente');
      setNewMessage('');
      setCategory('general');
      fetchTickets();
    } catch (err) {
      toast.error('Error al enviar solicitud');
    } finally {
      setSending(false);
    }
  };

  const handleUserReply = async (ticketId) => {
    if (!replyText.trim()) return;
    try {
      await apiClient.post(`/support/user-reply/${ticketId}`, {
        message: replyText.trim()
      });
      toast.success('Respuesta enviada');
      setReplyText('');
      setReplyingTo(null);
      fetchTickets();
    } catch (err) {
      toast.error('Error al enviar respuesta');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-testid="ticket-status-open">
            <Clock size={12} /> Pendiente
          </span>
        );
      case 'replied':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-testid="ticket-status-replied">
            <CheckCircle size={12} /> Respondido
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-MX', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="support-back-btn"
          >
            <ArrowLeft size={20} className="text-gray-700" />
          </button>
          <h1 className="text-lg font-bold text-gray-900" data-testid="support-title">Sugerencias y Soporte</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* New ticket form */}
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="support-new-ticket-form">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Enviar nueva solicitud</h2>
          <div className="mb-3">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
              data-testid="support-category-select"
            >
              <option value="general">General</option>
              <option value="error">Reportar error</option>
              <option value="sugerencia">Sugerencia</option>
              <option value="contenido">Contenido</option>
            </select>
          </div>
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Escribe tu mensaje aqui... (errores, sugerencias, comentarios)"
            className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-black min-h-[100px]"
            rows={4}
            data-testid="support-message-input"
          />
          <div className="flex justify-end mt-3">
            <button
              onClick={handleSubmit}
              disabled={sending || !newMessage.trim()}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="support-submit-btn"
            >
              <Send size={16} />
              {sending ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        </div>

        {/* Tickets list */}
        <div>
          <h2 className="text-base font-semibold text-gray-900 mb-3" data-testid="support-history-title">
            Mis solicitudes ({tickets.length})
          </h2>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Cargando...</div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-8 text-gray-400" data-testid="support-empty">
              No tienes solicitudes aun
            </div>
          ) : (
            <div className="space-y-4">
              {tickets.map((ticket) => (
                <div
                  key={ticket.ticket_id}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                  data-testid={`ticket-${ticket.ticket_id}`}
                >
                  {/* Ticket header - always visible */}
                  <div className="px-4 py-3">
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusBadge(ticket.status)}
                      <span className="text-xs text-gray-400 capitalize">{ticket.category}</span>
                    </div>
                    <p className="text-sm text-gray-800">{ticket.message}</p>
                    <p className="text-xs text-gray-400 mt-1">{formatDate(ticket.created_at)}</p>
                  </div>

                  {/* Replies - always visible */}
                  {ticket.replies && ticket.replies.length > 0 && (
                    <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 space-y-3">
                      {ticket.replies.map((reply, idx) => (
                        <div
                          key={reply.reply_id || idx}
                          className={`p-3 rounded-lg text-sm ${
                            reply.from_role === 'admin'
                              ? 'bg-black text-white ml-4'
                              : 'bg-white border border-gray-200 mr-4'
                          }`}
                          data-testid={`reply-${reply.reply_id || idx}`}
                        >
                          <p className={`text-xs font-medium mb-1 ${reply.from_role === 'admin' ? 'text-gray-300' : 'text-gray-500'}`}>
                            {reply.from_role === 'admin' ? 'Administrador' : 'Tu'}
                          </p>
                          <p className="whitespace-pre-wrap">{reply.message}</p>
                          <p className={`text-xs mt-1 ${reply.from_role === 'admin' ? 'text-gray-400' : 'text-gray-400'}`}>
                            {formatDate(reply.created_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply form - always visible at bottom */}
                  <div className="border-t border-gray-100 px-4 py-2.5 bg-gray-50">
                    {replyingTo === ticket.ticket_id ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Escribe tu respuesta..."
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                          onKeyDown={(e) => e.key === 'Enter' && handleUserReply(ticket.ticket_id)}
                          data-testid="support-reply-input"
                        />
                        <button
                          onClick={() => handleUserReply(ticket.ticket_id)}
                          disabled={!replyText.trim()}
                          className="px-3 py-2 bg-black text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-40"
                          data-testid="support-reply-send-btn"
                        >
                          <Send size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setReplyingTo(ticket.ticket_id)}
                        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-black transition-colors"
                        data-testid="support-reply-btn"
                      >
                        <MessageCircle size={14} /> Responder
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
